'use client';

import { useState, useRef, useEffect } from 'react';
import { Bell, Clock, Star, Calendar, CreditCard, X, AlertCircle } from 'lucide-react';
import Cookies from 'js-cookie';
import './Notification.css';
import { IconButton, Badge, Menu, MenuItem } from '@mui/material';
import NotificationsIcon from '../../assets/Notification/notification.svg';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
// import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import { axiosInstance } from '../../utils/tokenUtils';

import NewSessionIcon from '../../assets/Notification/clock.svg';
import ReviewIcon from '../../assets/Notification/star.svg';
import ScheduleChangeIcon from '../../assets/Notification/clock.svg';
import PaymentIcon from '../../assets/Notification/wallet.svg';
import CancelledIcon from '../../assets/Notification/session-cancelled.svg';
import ReminderIcon from '../../assets/Notification/reminder.svg';

export default function Notification() {
  const [open, setOpen] = useState(false);
  const [socket, setSocket] = useState(null);
  const notificationRef = useRef(null);
  const [connectionError, setConnectionError] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastReconnectTime, setLastReconnectTime] = useState(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_INTERVAL = 10 * 60 * 1000; // 10 minutes in milliseconds

  // Initialize notifications state
  const [notifications, setNotifications] = useState({
    today: [],
    yesterday: [],
    earlier: [],
  });

  // State for API-provided unread count
  const [serverUnreadCount, setServerUnreadCount] = useState(0);

  // Fetch previous notifications from the API
  const fetchPreviousNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/notifications');
      console.log('Previous notifications fetched:', response.data);

      if (response.data && Array.isArray(response.data)) {
        // Process notifications and categorize them by timeframe
        const processedNotifications = categorizeNotifications(response.data);
        setNotifications(processedNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Categorize notifications into today, yesterday, and earlier
  const categorizeNotifications = notificationList => {
    const categorized = {
      today: [],
      yesterday: [],
      earlier: [],
    };

    const now = new Date();

    notificationList.forEach(notification => {
      const notificationDate = new Date(notification.created_at);
      const diffDays = Math.floor((now - notificationDate) / (1000 * 60 * 60 * 24));

      // Format the notification time
      let timeString;
      if (diffDays === 0) {
        const diffHours = Math.floor((now - notificationDate) / (1000 * 60 * 60));
        if (diffHours === 0) {
          const diffMinutes = Math.floor((now - notificationDate) / (1000 * 60));
          timeString = `${diffMinutes} min ago`;
        } else {
          timeString = `${diffHours} hours ago`;
        }
      } else if (diffDays === 1) {
        timeString = `Yesterday at ${notificationDate.getHours()}:${notificationDate.getMinutes().toString().padStart(2, '0')} ${notificationDate.getHours() >= 12 ? 'PM' : 'AM'}`;
      } else {
        timeString = `${diffDays} days ago`;
      }

      // Create the notification object
      const formattedNotification = {
        id: notification.id,
        type: notification.type,
        notification_type: notification.notification_type,
        title: notification.title,
        message: notification.message,
        description: notification.description,
        time: timeString,
        read: Boolean(notification.is_read),
        created_at: notification.created_at,
      };

      // Add to the appropriate category
      if (diffDays === 0) {
        categorized.today.push(formattedNotification);
      } else if (diffDays === 1) {
        categorized.yesterday.push(formattedNotification);
      } else {
        categorized.earlier.push(formattedNotification);
      }
    });

    // Sort notifications in each category by created_at (newest first)
    Object.keys(categorized).forEach(key => {
      categorized[key].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    return categorized;
  };

  // Fetch unread notification count from API
  const fetchUnreadCount = async () => {
    try {
      const response = await axiosInstance.get('/notifications/unread-count');
      console.log('Unread notifications count fetched:', response.data);

      if (response.data && response.data['unread-notifications'] !== undefined) {
        setServerUnreadCount(response.data['unread-notifications']);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Fetch notifications and unread count when component mounts
  useEffect(() => {
    fetchPreviousNotifications();
    fetchUnreadCount();
  }, []);

  const connectWebSocket = async (forceTokenRefresh = false) => {
    // Close existing socket if it exists
    if (socket) {
      socket.close();
    }

    const now = Date.now();
    // Check if we've tried too recently (within 10 minutes)
    if (reconnectAttempts > 0 && now - lastReconnectTime < RECONNECT_INTERVAL) {
      console.log(`Skipping reconnection attempt - waiting for 10-minute interval to pass`);
      return;
    }

    // If we've reached the maximum number of attempts, don't try again
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.log(`Maximum reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Giving up.`);
      return;
    }

    // Update the last reconnection time
    setLastReconnectTime(now);

    // Get token from Cookies
    const token = Cookies.get('access_token');
    if (!token) {
      console.error('No token found in cookies');
      setConnectionError(true);
      return;
    }

    // Use the correct WebSocket URL format with the specified base URL
    const wsBaseUrl = process.env.REACT_APP_WS_BASE_URL;
    // Check if we need to use secure WebSocket protocol (wss) based on page protocol
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // Ensure the base URL doesn't include protocol
    // let cleanBaseUrl = wsBaseUrl;
    // if (
    //   cleanBaseUrl &&
    //   (cleanBaseUrl.startsWith('http://') || cleanBaseUrl.startsWith('https://'))
    // ) {
    //   cleanBaseUrl = cleanBaseUrl.split('//')[1];
    // } else if (
    //   cleanBaseUrl &&
    //   (cleanBaseUrl.startsWith('ws://') || cleanBaseUrl.startsWith('wss://'))
    // ) {
    //   cleanBaseUrl = cleanBaseUrl.split('//')[1];
    // }

    const wsUrl = `ws://dev.api.klass.online/notifications/ws?token=${token}`;
    console.log('Connecting to Notification WebSocket:', wsUrl);

    const newSocket = new WebSocket(wsUrl);

    newSocket.onopen = () => {
      console.log('Notification WebSocket connected successfully');
      setSocket(newSocket);
      setConnectionError(false);
      setReconnectAttempts(0); // Reset reconnect attempts on successful connection
    };

    newSocket.onmessage = event => {
      console.log('Notification WebSocket message received:', event.data);
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'new_notification') {
          // Handle new notification
          addNewNotification(data.notification);
        } else if (data.type === 'notification_read') {
          // Handle notification read
          markNotificationAsRead(data.notification_id);
        } else if (data.type === 'notification_deleted') {
          // Simply ignore this notification type now
        } else if (data.msg && data.title) {
          // Handle direct message format
          const notification = {
            id: Date.now(), // Generate a unique ID
            type: data.title.toLowerCase().includes('cancelled') ? 'cancelled' : 'general',
            title: data.title,
            message: data.msg,
            created_at: new Date().toISOString(),
            read: false,
          };
          addNewNotification(notification);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    newSocket.onerror = error => {
      console.error('WebSocket error:', error);
      setConnectionError(true);
    };

    newSocket.onclose = event => {
      console.log('WebSocket closed:', event);
      setSocket(null);

      // Attempt to reconnect after a delay, but only if we haven't exceeded max attempts
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const nextAttempt = reconnectAttempts + 1;
        setReconnectAttempts(nextAttempt);

        console.log(`WebSocket reconnection attempt ${nextAttempt}/${MAX_RECONNECT_ATTEMPTS}`);
      } else {
        console.log(`Maximum WebSocket reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached`);
      }
    };
  };

  // Connect to WebSocket when component mounts
  useEffect(() => {
    connectWebSocket();

    // Clean up WebSocket connection when component unmounts
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, []);

  // Reset reconnection attempts periodically
  useEffect(() => {
    // Reset reconnection attempts after 1 hour
    const resetInterval = setInterval(
      () => {
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Resetting WebSocket reconnection attempts counter');
          setReconnectAttempts(0);
        }
      },
      60 * 60 * 1000
    ); // 1 hour

    return () => clearInterval(resetInterval);
  }, [reconnectAttempts]);

  // Handle connection errors by refreshing token

  const addNewNotification = notification => {
    // When a new notification comes in via WebSocket,
    // add it to our notifications state

    // Ensure notification has the necessary fields
    const processedNotification = {
      ...notification,
      type: notification.type,
      notification_type: notification.notification_type,
      message: notification.message || notification.msg,
      description: notification.description,
      title: notification.title,
      read: notification.read || false,
      is_read: notification.is_read || false,
      created_at: notification.created_at || new Date().toISOString(),
    };

    const processedNotifications = categorizeNotifications([processedNotification]);

    setNotifications(prev => {
      const newNotifications = { ...prev };

      // Merge the new notification into the existing categories
      Object.keys(processedNotifications).forEach(timeframe => {
        if (processedNotifications[timeframe].length > 0) {
          newNotifications[timeframe] = [...processedNotifications[timeframe], ...prev[timeframe]];
        }
      });

      return newNotifications;
    });
  };

  const markNotificationAsRead = async notificationId => {
    const updatedNotifications = { ...notifications };

    Object.keys(updatedNotifications).forEach(timeframe => {
      updatedNotifications[timeframe] = updatedNotifications[timeframe].map(notification =>
        notification.id === notificationId ? { ...notification, read: true } : notification
      );
    });

    setNotifications(updatedNotifications);

    // Update server unread count
    fetchUnreadCount();

    // Send read status to server via API as well
    try {
      await axiosInstance.post(`/notifications/${notificationId}/read`);
      console.log(`Notification ${notificationId} marked as read on server via API`);
    } catch (error) {
      console.error(`Failed to mark notification ${notificationId} as read on server:`, error);
    }
  };

  const handleOpen = event => {
    // Fetch latest notifications and unread count when opening the panel
    fetchPreviousNotifications();
    fetchUnreadCount();

    setOpen(true);

    // Mark notifications as read when opening the panel
    if (!open) {
      // Get all unread notification IDs
      const unreadIds = [];
      Object.values(notifications).forEach(section => {
        section.forEach(notification => {
          if (!notification.read) {
            unreadIds.push(notification.id);
          }
        });
      });

      // Send read status to server via both WebSocket and API
      if (unreadIds.length > 0) {
        // Send via WebSocket if connected
        if (socket) {
          socket.send(
            JSON.stringify({
              type: 'mark_read',
              notification_ids: unreadIds,
            })
          );
        }

        // Also mark as read via API
        unreadIds.forEach(async id => {
          try {
            await axiosInstance.post(`/notifications/${id}/read`);
          } catch (error) {
            console.error(`Failed to mark notification ${id} as read:`, error);
          }
        });
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    const updatedNotifications = { ...notifications };
    const unreadIds = [];

    Object.keys(updatedNotifications).forEach(timeframe => {
      updatedNotifications[timeframe] = updatedNotifications[timeframe].map(notification => {
        if (!notification.read) {
          unreadIds.push(notification.id);
        }
        return {
          ...notification,
          read: true,
        };
      });
    });

    setNotifications(updatedNotifications);
    setServerUnreadCount(0); // Update server unread count in UI immediately

    // Send read status to server via both methods
    if (unreadIds.length > 0) {
      // Send via WebSocket if connected
      if (socket) {
        socket.send(
          JSON.stringify({
            type: 'mark_read',
            notification_ids: unreadIds,
          })
        );
      }

      // Also mark as read via API - updated endpoint
      try {
        const response = await axiosInstance.post(`/notifications/mark-all-as-read`);
        console.log('All notifications marked as read on server via API:', response.data);
      } catch (error) {
        console.error('Failed to mark all notifications as read:', error);
      }
    }
  };

  const getNotificationIcon = type => {
    switch (type) {
      case 'booking_created':
        return (
          <div className="notification-icon-circle session-booked">
            <img src={NewSessionIcon} className="notification-icon" alt="New Booking" />
          </div>
        );
      case 'session_booked':
        return (
          <div className="notification-icon-circle session-booked">
            <img src={NewSessionIcon} className="notification-icon" alt="Session Booked" />
          </div>
        );
      case 'review':
        return (
          <div className="notification-icon-circle review">
            <img src={ReviewIcon} className="notification-icon" alt="Review" />
          </div>
        );
      case 'schedule_change':
        return (
          <div className="notification-icon-circle schedule-change">
            <img src={ScheduleChangeIcon} className="notification-icon" alt="Schedule Change" />
          </div>
        );
      case 'payment':
        return (
          <div className="notification-icon-circle payment">
            <img src={PaymentIcon} className="notification-icon" alt="Payment" />
          </div>
        );
      case 'cancelled':
        return (
          <div className="notification-icon-circle cancelled">
            <img src={CancelledIcon} className="notification-icon" alt="Cancelled" />
          </div>
        );
      case 'reminder':
        return (
          <div className="notification-icon-circle reminder">
            <img src={ReminderIcon} className="notification-icon" alt="Reminder" />
          </div>
        );
      default:
        return (
          <div className="notification-icon-circle general">
            <img src={NotificationsIcon} className="notification-icon" alt="Notification" />
          </div>
        );
    }
  };

  // Count unread notifications - use server count if available, otherwise use local count
  const unreadCount =
    serverUnreadCount !== undefined && serverUnreadCount !== null
      ? serverUnreadCount
      : Object.values(notifications)
          .flat()
          .filter(notification => !notification.read).length;

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format notification text to be more presentable
  const formatNotificationText = text => {
    if (!text) return '';

    // First, handle special case replacements
    const specialReplacements = {
      'SessionType.ONE_ON_ONE': 'One-on-One',
      'one_on_one Session': 'One-on-One Session',
      one_on_one: 'One-on-One',
    };

    let formattedText = text;

    // Apply special replacements first
    Object.entries(specialReplacements).forEach(([key, value]) => {
      formattedText = formattedText.replace(new RegExp(key, 'g'), value);
    });

    // Replace remaining underscores with spaces and capitalize each word
    formattedText = formattedText
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    // Handle cases where we already have spaces
    formattedText = formattedText
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return formattedText;
  };

  return (
    <div className={`notification-container ${open ? 'notification-container-active' : ''}`}>
      <IconButton onClick={handleOpen} aria-label="notifications">
        <Badge badgeContent={unreadCount} color="error">
          <img src={NotificationsIcon} />
        </Badge>
      </IconButton>

      {open && (
        <>
          <div className="notification-backdrop" onClick={handleClose}></div>
          <div className="notification-popup" ref={notificationRef}>
            <div className="notification-header">
              <h3>Notifications</h3>
              <button className="mark-all-button" onClick={handleMarkAllAsRead}>
                Mark all as read
              </button>
            </div>

            <div className="notification-content-container">
              {error && (
                <div className="notification-error">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                  <button onClick={fetchPreviousNotifications}>Retry</button>
                </div>
              )}

              {/* Today's notifications */}
              {notifications.today.length > 0 && (
                <>
                  <div className="notification-section-title">Today</div>
                  {notifications.today.map(notification => (
                    <div key={notification.id} className="notification-item">
                      {!notification.read && <div className="unread-indicator"></div>}
                      <div className="notification-content">
                        {getNotificationIcon(notification.type || notification.notification_type)}
                        <div className="notification-details">
                          <div className="notification-title">
                            {formatNotificationText(notification.title)}
                          </div>
                          <div className="notification-message">
                            {formatNotificationText(
                              notification.message || notification.description
                            )}
                          </div>
                          <div className="notification-time">
                            <AccessTimeIcon fontSize="small" /> {notification.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Yesterday's notifications */}
              {notifications.yesterday.length > 0 && (
                <>
                  <div className="notification-section-title">Yesterday</div>
                  {notifications.yesterday.map(notification => (
                    <div key={notification.id} className="notification-item">
                      {!notification.read && <div className="unread-indicator"></div>}
                      <div className="notification-content">
                        {getNotificationIcon(notification.type || notification.notification_type)}
                        <div className="notification-details">
                          <div className="notification-title">
                            {formatNotificationText(notification.title)}
                          </div>
                          <div className="notification-message">
                            {formatNotificationText(
                              notification.message || notification.description
                            )}
                          </div>
                          <div className="notification-time">
                            <AccessTimeIcon fontSize="small" /> {notification.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Earlier notifications */}
              {notifications.earlier.length > 0 && (
                <>
                  <div className="notification-section-title">Earlier</div>
                  {notifications.earlier.map(notification => (
                    <div key={notification.id} className="notification-item">
                      {!notification.read && <div className="unread-indicator"></div>}
                      <div className="notification-content">
                        {getNotificationIcon(notification.type || notification.notification_type)}
                        <div className="notification-details">
                          <div className="notification-title">
                            {formatNotificationText(notification.title)}
                          </div>
                          <div className="notification-message">
                            {formatNotificationText(
                              notification.message || notification.description
                            )}
                          </div>
                          <div className="notification-time">
                            <AccessTimeIcon fontSize="small" /> {notification.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Empty state if there are no notifications */}
              {Object.values(notifications).flat().length === 0 && (
                <div className="empty-notifications">
                  <p>No notifications at this time</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
